import { ReactElement, useEffect, useRef, useState } from 'react';
import sizeMe, { SizeMeProps } from 'react-sizeme';

import gtmIds, { dataId } from 'adapter/in/dashboards/config/gtm';
import { StyledGrabbale } from 'adapter/in/dashboards/pages/Widget/Wrapper/style';
import WidgetActionsMenuContainer from 'adapter/in/common/containers/Widget/WidgetActionsMenu';
import { WidgetType } from 'domain/entity/widget';
import Branding from 'domain/entity/branding';
import { ConstraintsPerLayoutElement, LayoutType } from 'domain/entity/layout';
import { KeyAndNamePair } from 'adapter/in/common/context/currentWidgetConfiguration';

import SmallAlertTooltip from 'adapter/in/dashboards/components/SmallAlertTooltip';
import { AnalysisActionsMenuContainer } from 'adapter/in/common/containers/Widget/AnalysisActionsMenu/AnalysisActionsMenu';
import { GridOrigin } from 'adapter/in/dashboards/containers/Grid';
import ReportAnalysisActionsMenuContainer from 'adapter/in/common/containers/Widget/ReportAnalysisActionsMenu/ReportAnalysisActionsMenu';
import { TextBlockActionsMenuContainer } from 'adapter/in/common/containers/Widget/TextBlockActionsMenu/TextBlockActionsMenu';
import { BusinessInsightActionsMenuContainer } from 'adapter/in/common/containers/Widget/BusinessInsightActionsMenu/BusinessInsightActionsMenu';
import { useTextBlockData } from 'adapter/in/reports/contexts/TextBlockContext';
import { isDashboardFrame } from 'adapter/in/common/widget/widgetBuilder';
import AnalysisAiDescriptionReadOnly from 'adapter/in/common/components/AnalysisAiDescriptionReadOnly';
import { WidgetIssue } from 'domain/entity/widgetIssue';
import { dispatchWidgetIssues } from 'adapter/in/reports/contexts/WidgetIssuesContext';
import WidgetIssueBadges from 'adapter/in/reports/components/WidgetIssueBadges';

type WidgetWrapperProps = {
    isEditingLayout: boolean;
    isEditEnabled: boolean;
    hasWidgetDataError: boolean;
    WidgetBuilder: (
        width: number,
        height: number,
        layoutType?: LayoutType,
        editable?: boolean,
        sizeIssue?: ConstraintsPerLayoutElement,
        widgetWidth?: number,
        widgetHeight?: number,
        isReportTemplate?: boolean
    ) => ReactElement;
    dashboardId: string;
    widgetId: string;
    widgetType: WidgetType;
    isSelectedWidget: boolean;
    branding: Branding;
    layoutType: LayoutType;
    newPreset: KeyAndNamePair;
    newFeature: KeyAndNamePair;
    sizeIssue?: ConstraintsPerLayoutElement;
    alertTooltip?: string;
    gridOrigin?: GridOrigin;
    isReportInstance?: boolean;
    isAnalysisWidget?: boolean;
    aiDescription?: string;
    aiDescriptionLoading?: boolean;
    widgetWidth?: number;
    widgetHeight?: number;
    isReportTemplate?: boolean;
    widgetIssues?: WidgetIssue[];
    widgetTitle?: string;
    widgetPage?: number;
};

const sizeMeHOC = sizeMe({
    monitorWidth: true,
    monitorHeight: true,
    refreshRate: 300,
});

const AI_CHART_MIN_RATIO = 0.6;

const WidgetWrapper = (props: WidgetWrapperProps & SizeMeProps): ReactElement => {
    const {
        WidgetBuilder,
        size,
        isEditingLayout,
        isEditEnabled,
        dashboardId,
        widgetId,
        widgetType,
        isSelectedWidget,
        branding,
        layoutType,
        newPreset,
        newFeature,
        hasWidgetDataError,
        sizeIssue,
        alertTooltip,
        gridOrigin = GridOrigin.DASHBOARD,
        isReportInstance,
        isAnalysisWidget,
        aiDescription,
        aiDescriptionLoading,
        widgetWidth,
        widgetHeight,
        isReportTemplate,
        widgetIssues,
        widgetTitle,
        widgetPage,
    } = props;

    const [showMenu, setShowMenu] = useState(false);
    const { editorBeingEdited } = useTextBlockData();
    const { openWidgetIssues } = dispatchWidgetIssues();

    const renderActionsMenu = () => {
        if (widgetType === WidgetType.ANALYSIS && gridOrigin === GridOrigin.DASHBOARD) {
            return <AnalysisActionsMenuContainer dashboardId={dashboardId} analysisId={widgetId} showMenu={showMenu} />;
        }
        if (widgetType === WidgetType.ANALYSIS && gridOrigin === GridOrigin.REPORT) {
            return <ReportAnalysisActionsMenuContainer dashboardId={dashboardId} analysisId={widgetId} showMenu={showMenu} />;
        }
        if (widgetType === WidgetType.RICH_TEXT) {
            return editorBeingEdited?.textBlockId !== widgetId ? (
                <TextBlockActionsMenuContainer dashboardId={dashboardId} widgetId={widgetId} showMenu={showMenu} />
            ) : null;
        }
        if (widgetType === WidgetType.BUSINESS_INSIGHT) {
            return <BusinessInsightActionsMenuContainer dashboardId={dashboardId} widgetId={widgetId} showMenu={showMenu} />;
        }
        return (
            <WidgetActionsMenuContainer
                showMenu={showMenu}
                showOptions={!hasWidgetDataError}
                newPreset={newPreset}
                newFeature={newFeature}
                dashboardId={dashboardId}
                widgetId={widgetId}
                widgetType={widgetType}
            />
        );
    };

    const dashboardFrame = isDashboardFrame(widgetId);

    const hasAiDescription = !!isAnalysisWidget && (!!aiDescription || !!aiDescriptionLoading);
    const descriptionRef = useRef<HTMLDivElement | null>(null);
    const [descriptionHeight, setDescriptionHeight] = useState(0);

    useEffect(() => {
        const node = descriptionRef.current;
        if (!hasAiDescription || !node) {
            setDescriptionHeight(0);
            return undefined;
        }
        const updateHeight = () => setDescriptionHeight(node.offsetHeight);
        updateHeight();
        if (typeof ResizeObserver === 'undefined') {
            return undefined;
        }
        const observer = new ResizeObserver(updateHeight);
        observer.observe(node);
        return () => observer.disconnect();
    }, [hasAiDescription, aiDescription, size.width]);

    const maxDescriptionHeight = hasAiDescription && size.height ? Math.round(size.height * (1 - AI_CHART_MIN_RATIO)) : undefined;
    const chartHeight =
        hasAiDescription && size.height
            ? Math.max(Math.round(size.height * AI_CHART_MIN_RATIO), size.height - descriptionHeight)
            : size.height!;

    return (
        <StyledGrabbale
            data-testid='widget-wrapper'
            data-id={`${dataId}.${gtmIds.widget.notEditable}`}
            $editable={isEditingLayout}
            $isEditEnabled={isEditEnabled}
            $isWidgetSelected={isSelectedWidget}
            $isDashboardFrame={dashboardFrame}
            $branding={branding}
            $gridOrigin={gridOrigin}
            $isEditingLayout={isEditingLayout}
            $isReportInstance={isReportInstance}
            onMouseEnter={() => setShowMenu(true)}
            onMouseLeave={() => setShowMenu(false)}
        >
            {alertTooltip && <SmallAlertTooltip tooltipMessage={alertTooltip} />}
            {gridOrigin === GridOrigin.REPORT && !!widgetIssues?.length && (
                <WidgetIssueBadges
                    issues={widgetIssues}
                    onOpen={() => openWidgetIssues?.({ widgetId, widgetType, widgetTitle, page: widgetPage, issues: widgetIssues })}
                />
            )}
            {isEditEnabled && !isEditingLayout && !dashboardFrame && renderActionsMenu()}
            {WidgetBuilder(size.width!, chartHeight, layoutType, isEditingLayout, sizeIssue, widgetWidth, widgetHeight, isReportTemplate)}
            {hasAiDescription && (
                <AnalysisAiDescriptionReadOnly
                    ref={descriptionRef}
                    text={aiDescription}
                    isLoading={aiDescriptionLoading}
                    maxHeight={maxDescriptionHeight}
                />
            )}
        </StyledGrabbale>
    );
};

export default sizeMeHOC(WidgetWrapper);
